/**
 * Dashboard store - manages dashboard state
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  dashboardApi,
  type DashboardStats,
  type ActivityItem,
  type TaskDueSoon,
  type ProjectSummary,
  type MyTask,
} from '@services/api';
import type { Meeting } from '@services/api';

interface DashboardState {
  // State
  stats: DashboardStats | null;
  recentActivity: ActivityItem[];
  tasksDueSoon: TaskDueSoon[];
  myTasks: MyTask[];
  projects: ProjectSummary[];
  upcomingMeetings: Meeting[];
  isLoading: boolean;
  error: string | null;
  lastFetched: Date | null;

  // Actions
  fetchDashboard: (organizationId: string) => Promise<void>;
  fetchStats: (organizationId: string) => Promise<void>;
  fetchActivity: (organizationId: string, limit?: number) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  stats: null as DashboardStats | null,
  recentActivity: [] as ActivityItem[],
  tasksDueSoon: [] as TaskDueSoon[],
  myTasks: [] as MyTask[],
  projects: [] as ProjectSummary[],
  upcomingMeetings: [] as Meeting[],
  isLoading: false,
  error: null as string | null,
  lastFetched: null as Date | null,
};

export const useDashboardStore = create<DashboardState>()(
  immer((set) => ({
    ...initialState,

    fetchDashboard: async (organizationId: string) => {
      // Validate organizationId is a valid UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!organizationId || !uuidRegex.test(organizationId)) {
        set((state) => {
          state.error = 'Invalid organization ID';
          state.isLoading = false;
        });
        return;
      }

      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const data = await dashboardApi.getDashboard(organizationId);
        set((state) => {
          state.stats = data.stats;
          state.recentActivity = data.recentActivity;
          state.tasksDueSoon = data.tasksDueSoon;
          state.myTasks = data.myTasks;
          state.projects = data.projects;
          state.upcomingMeetings = data.upcomingMeetings;
          state.isLoading = false;
          state.lastFetched = new Date();
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch dashboard';
          state.isLoading = false;
        });
      }
    },

    fetchStats: async (organizationId: string) => {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!organizationId || !uuidRegex.test(organizationId)) {
        return;
      }
      try {
        const stats = await dashboardApi.getStats(organizationId);
        set((state) => {
          state.stats = stats;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch stats';
        });
      }
    },

    fetchActivity: async (organizationId: string, limit = 20) => {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!organizationId || !uuidRegex.test(organizationId)) {
        return;
      }
      try {
        const activity = await dashboardApi.getActivity(organizationId, limit);
        set((state) => {
          state.recentActivity = activity;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch activity';
        });
      }
    },

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);
